import { Injectable } from '@angular/core';

declare global {
  interface Window {
    grecaptcha?: {
      render: (
        container: HTMLElement,
        parameters: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ) => number;
      reset: (widgetId?: number) => void;
    };
    onRecaptchaScriptLoad?: () => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private loadPromise?: Promise<void>;

  load(): Promise<void> {
    if (window.grecaptcha?.render) {
      return Promise.resolve();
    }

    this.loadPromise ??= new Promise((resolve, reject) => {
      window.onRecaptchaScriptLoad = resolve;

      const script = document.createElement('script');
      script.src =
        'https://www.google.com/recaptcha/api.js?onload=onRecaptchaScriptLoad&render=explicit';
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        this.loadPromise = undefined;
        window.onRecaptchaScriptLoad = undefined;
        reject(new Error('Failed to load reCAPTCHA'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  async render(
    container: HTMLElement,
    siteKey: string,
    callback: (token: string) => void,
    expired: () => void,
  ): Promise<number> {
    await this.load();

    if (!window.grecaptcha?.render) {
      throw new Error('reCAPTCHA failed to load');
    }

    return window.grecaptcha.render(container, {
      sitekey: siteKey,
      callback,
      'expired-callback': expired,
      'error-callback': expired,
    });
  }

  reset(widgetId?: number): void {
    window.grecaptcha?.reset(widgetId);
  }
}
